<template>
    <app-panel :title="`${$t('Slot')}: ${slotTitle}`" panel-class="overflow-visible" body-class="!p-0 overflow-visible">
        <template #header-actions>
            <div class="flex items-center gap-3">
                <button
                    v-if="group.allow_instancing"
                    class="btn-stage-secondary text-xs"
                    type="button"
                    @click="openPickerDialog(group)"
                >
                    {{ $t('Modul instanzieren') }}
                </button>
                <button
                    class="btn-stage-primary text-xs"
                    type="button"
                    @click="openTypeDialog(group)"
                >
                    {{ $t('Modul anlegen') }}
                </button>
            </div>
        </template>

        <div class="overflow-visible">
                <table class="table-listview table-listview--zebra">
                    <tbody class="bg-white">
                    <tr
                        v-for="item in group.items"
                        :key="item.placement_id"
                        class="group"
                        @dragover.prevent
                        @drop="onDrop(group, item.placement_id)"
                    >
                        <td class="table-listview-l-td whitespace-nowrap" style="padding-right: 0;">
                            <button
                                class="inline-flex cursor-grab items-center justify-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                                type="button"
                                draggable="true"
                                :title="$t('Sortieren')"
                                :aria-label="$t('Sortieren')"
                                @click.stop
                                @dragstart="onDragStart(group.slot, item.placement_id)"
                                @dragend="resetDragState"
                            >
                                <svg-vue
                                    class="h-4 w-4"
                                    icon="font-awesome.regular.bars"
                                    aria-hidden="true"
                                />
                            </button>
                        </td>
                        <td class="table-listview-l-td cursor-pointer" style="padding-left: 0.25rem;" @click="openModule(item)">
                            <div class="min-w-0 truncate text-sm font-medium text-gray-900">
                                {{ item.name }}
                                <span v-if="item.module_type_name" class="font-normal text-gray-500">({{ item.module_type_name }})</span>
                            </div>
                        </td>
                        <td class="table-listview-l-td w-52 cursor-pointer whitespace-nowrap text-sm text-gray-500" @click="openModule(item)">
                            {{ typeLabel(item) }}
                        </td>
                        <td class="table-listview-l-td w-36 whitespace-nowrap text-right">
                            <button
                                class="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 focus:outline-none"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :data-cms-slot-action-trigger="item.placement_id"
                                @click.stop="toggleActionMenu(item, $event)"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
        </div>

        <div
            v-if="actionMenu.placementId"
            ref="actionMenu"
            class="fixed z-[9999] w-40 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
            :style="actionMenuStyle"
            @click.stop
        >
            <button
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="openActionMenuModule"
            >
                {{ $t('Bearbeiten') }}
            </button>
            <button
                v-if="contextType === 'page'"
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="openCopyToPageDialog(actionMenu.item)"
            >
                {{ $t('Auf andere Seite kopieren') }}
            </button>
            <button
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="requestDuplicatePlacement(actionMenu.item)"
            >
                {{ $t('Duplizieren') }}
            </button>
            <button
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="removeActionMenuModule"
            >
                {{ $t('Löschen') }}
            </button>
        </div>

        <module-picker-dialog
            :open="pickerDialog.open"
            :title="$t('Modul instanzieren')"
            :text="$t('Ein bestehendes Modul wird in diesen Slot eingebunden.')"
            :modules="pickerDialog.modules"
            :loading="pickerDialog.loading"
            headline-id="cms-slot-module-picker-headline"
            @close="closePickerDialog"
            @select="attachExistingModule"
        />

        <module-type-picker-dialog
            :open="typeDialog.open"
            :module-types="typeDialog.moduleTypes"
            :text="$t('Für diesen Slot wird ein neues Modul auf Basis des gewählten Presets angelegt.')"
            headline-id="cms-slot-type-picker-headline"
            @close="closeTypeDialog"
            @select="createModule"
        />

        <module-copy-to-page-dialog
            :open="copyDialog.open"
            :loading="copyDialog.loading"
            :pages="copyDialog.pages"
            :selected-page-id="copyDialog.selectedPageId"
            :selected-slot="copyDialog.selectedSlot"
            headline-id="cms-slot-copy-to-page-headline"
            @close="closeCopyDialog"
            @confirm="confirmCopyToPage"
            @update:selectedPageId="updateCopyDialogPageId"
            @update:selectedSlot="updateCopyDialogSlot"
        />

        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-slot-table-confirm-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </app-panel>
</template>

<script>
import ConfirmDialog from "@/components/modals/confirm-dialog";
import ModuleCopyToPageDialog from "@/components/modals/module-copy-to-page-dialog";
import ModulePickerDialog from "@/components/modals/module-picker-dialog";
import ModuleTypePickerDialog from "@/components/modals/module-type-picker-dialog";

export default {
    name: 'cms-slot-table',
    components: {
        ConfirmDialog,
        ModuleCopyToPageDialog,
        ModulePickerDialog,
        ModuleTypePickerDialog,
    },
    props: {
        group: {
            type: Object,
            required: true,
        },
        contextType: {
            type: String,
            required: true,
        },
        contextId: {
            type: [Number, String],
            required: true,
        },
        moduleTypeOptions: {
            type: Array,
            default: () => [],
        },
        navigationContext: {
            type: Object,
            default: () => ({
                originPath: '/workspace/cms/module-library',
                modulePath: [],
            }),
        },
    },
    emits: ['changed', 'reordered'],
    data() {
        return {
            draggedSlot: null,
            draggedPlacementId: null,
            actionMenu: {
                placementId: null,
                item: null,
                top: 0,
                left: 0,
                openUpward: false,
            },
            pickerDialog: {
                open: false,
                loading: false,
                slot: null,
                modules: [],
            },
            typeDialog: {
                open: false,
                slot: null,
                moduleTypes: [],
            },
            copyDialog: {
                open: false,
                loading: false,
                item: null,
                pages: [],
                selectedPageId: null,
                selectedSlot: '',
            },
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
        };
    },
    computed: {
        slotTitle() {
            return String(this.group.label || this.group.slot || '').toUpperCase();
        },
        actionMenuStyle() {
            const transform = this.actionMenu.openUpward
                ? 'translate(-100%, calc(-100% - 8px))'
                : 'translate(-100%, 0)';

            return {
                top: this.actionMenu.openUpward ? `${this.actionMenu.top}px` : `${this.actionMenu.top + 8}px`,
                left: `${this.actionMenu.left}px`,
                transform,
            };
        },
        filteredTypeOptions() {
            const allowedKeys = Array.isArray(this.group.allowed_module_type_keys)
                ? this.group.allowed_module_type_keys
                : [];

            if (allowedKeys.length < 1) {
                return Array.isArray(this.moduleTypeOptions) ? this.moduleTypeOptions : [];
            }

            return (Array.isArray(this.moduleTypeOptions) ? this.moduleTypeOptions : []).filter((moduleType) => {
                return allowedKeys.includes(moduleType.key);
            });
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        window.addEventListener('resize', this.closeActionMenu);
        window.addEventListener('scroll', this.closeActionMenu, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
        window.removeEventListener('resize', this.closeActionMenu);
        window.removeEventListener('scroll', this.closeActionMenu, true);
    },
    methods: {
        typeLabel(item) {
            return item.is_global ? this.$t('Globale Modul Instanz') : this.$t('Seiten Modul');
        },
        openModule(item) {
            this.closeActionMenu();
            this.$router.push(this.editPath(item));
        },
        toggleActionMenu(item, event) {
            if (this.actionMenu.placementId === item.placement_id) {
                this.closeActionMenu();
                return;
            }

            const rect = event.currentTarget.getBoundingClientRect();
            const estimatedHeight = 96;
            const hasSpaceBelow = (window.innerHeight - rect.bottom) >= (estimatedHeight + 16);

            this.actionMenu = {
                placementId: item.placement_id,
                item,
                top: hasSpaceBelow ? rect.bottom : rect.top,
                left: rect.right,
                openUpward: !hasSpaceBelow,
            };
        },
        closeActionMenu() {
            this.actionMenu = {
                placementId: null,
                item: null,
                top: 0,
                left: 0,
                openUpward: false,
            };
        },
        handleDocumentClick(event) {
            if (!this.actionMenu.placementId) {
                return;
            }

            const menuElement = this.$refs.actionMenu;
            const triggerSelector = `[data-cms-slot-action-trigger="${this.actionMenu.placementId}"]`;
            const triggerElement = event.target.closest(triggerSelector);

            if ((menuElement && menuElement.contains(event.target)) || triggerElement) {
                return;
            }

            this.closeActionMenu();
        },
        openActionMenuModule() {
            if (this.actionMenu.item) {
                this.openModule(this.actionMenu.item);
            }
        },
        removeActionMenuModule() {
            if (this.actionMenu.item) {
                this.requestRemovePlacement(this.actionMenu.item);
            }
        },
        editPath(item) {
            const query = {};
            const originPath = String(this.navigationContext?.originPath || '').trim();
            const modulePath = Array.isArray(this.navigationContext?.modulePath)
                ? this.navigationContext.modulePath
                : [];

            if (originPath !== '') {
                query.origin_path = originPath;
            }

            if (modulePath.length > 0) {
                query.module_path = modulePath.join(',');
            }

            return {
                path: '/workspace/cms/module-library/' + item.module_id + '/edit',
                query,
            };
        },
        openPickerDialog(group) {
            this.pickerDialog = {
                open: true,
                loading: true,
                slot: group.slot,
                modules: [],
            };

            axios.get('api/workspace/admin/cms/modules/picker-options', {
                params: {
                    allowed_module_type_keys: Array.isArray(group.allowed_module_type_keys) ? group.allowed_module_type_keys : [],
                },
            }).then((response) => {
                this.pickerDialog.modules = Array.isArray(response.data.modules) ? response.data.modules : [];
                this.pickerDialog.loading = false;
            }).catch(() => {
                this.pickerDialog.loading = false;
            });
        },
        closePickerDialog() {
            this.pickerDialog = {
                open: false,
                loading: false,
                slot: null,
                modules: [],
            };
        },
        attachExistingModule(module) {
            const slot = this.pickerDialog.slot;
            if (!slot || !module?.id) {
                this.closePickerDialog();
                return;
            }

            axios.post(this.attachEndpoint(slot), {
                cms_module_id: module.id,
            }).then(() => {
                this.closePickerDialog();
                this.$emit('changed');
            });
        },
        openTypeDialog(group) {
            if (this.filteredTypeOptions.length === 1) {
                this.createModule(this.filteredTypeOptions[0], group.slot);
                return;
            }

            this.typeDialog = {
                open: true,
                slot: group.slot,
                moduleTypes: this.filteredTypeOptions,
            };
        },
        closeTypeDialog() {
            this.typeDialog = {
                open: false,
                slot: null,
                moduleTypes: [],
            };
        },
        openCopyToPageDialog(item) {
            if (this.contextType !== 'page' || !item?.placement_id) {
                return;
            }

            this.closeActionMenu();
            this.copyDialog = {
                open: true,
                loading: true,
                item,
                pages: [],
                selectedPageId: null,
                selectedSlot: '',
            };

            axios.get('api/workspace/admin/cms/module-placements/' + item.placement_id + '/copy-targets').then((response) => {
                this.copyDialog.pages = Array.isArray(response.data.pages) ? response.data.pages : [];
                this.copyDialog.loading = false;
            }).catch(() => {
                this.copyDialog.loading = false;
            });
        },
        closeCopyDialog() {
            this.copyDialog = {
                open: false,
                loading: false,
                item: null,
                pages: [],
                selectedPageId: null,
                selectedSlot: '',
            };
        },
        updateCopyDialogPageId(pageId) {
            this.copyDialog.selectedPageId = pageId;
            this.copyDialog.selectedSlot = '';
        },
        updateCopyDialogSlot(slot) {
            this.copyDialog.selectedSlot = String(slot || '');
        },
        confirmCopyToPage() {
            if (!this.copyDialog.item?.placement_id || !this.copyDialog.selectedPageId || this.copyDialog.selectedSlot.trim() === '') {
                return;
            }

            this.copyDialog.loading = true;

            axios.post('api/workspace/admin/cms/module-placements/' + this.copyDialog.item.placement_id + '/copy-to-page', {
                cms_page_id: this.copyDialog.selectedPageId,
                slot: this.copyDialog.selectedSlot,
            }).then(() => {
                this.closeCopyDialog();
                this.$notify({
                    title: this.$t('Erfolg'),
                    text: this.$t('Modul erfolgreich auf Zielseite kopiert'),
                    type: 'success',
                });
            }).catch(() => {
                this.copyDialog.loading = false;
            });
        },
        createModule(moduleType, forcedSlot = null) {
            const slot = forcedSlot || this.typeDialog.slot;
            if (!slot || !moduleType?.id) {
                this.closeTypeDialog();
                return;
            }

            axios.post(this.createEndpoint(slot), {
                cms_module_type_id: moduleType.id,
            }).then(() => {
                this.closeTypeDialog();
                this.$emit('changed');
            });
        },
        requestRemovePlacement(item) {
            this.closeActionMenu();
            this.confirmDialog = {
                open: true,
                title: item.is_global ? this.$t('Globale Instanz entfernen') : this.$t('Modul löschen'),
                text: item.is_global
                    ? this.$t('Soll diese globale Modul-Instanz wirklich aus dem Slot entfernt werden?')
                    : this.$t('Soll dieses Seiten Modul wirklich gelöscht werden?'),
                pendingAction: () => axios.delete('api/workspace/admin/cms/module-placements/' + item.placement_id).then(() => {
                    this.$emit('changed');
                }),
            };
        },
        requestDuplicatePlacement(item) {
            this.closeActionMenu();
            axios.post('api/workspace/admin/cms/module-placements/' + item.placement_id + '/duplicate').then(() => {
                this.$emit('changed');
            });
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
        onDragStart(slot, placementId) {
            this.draggedSlot = slot;
            this.draggedPlacementId = placementId;
        },
        onDrop(group, targetPlacementId) {
            if (!this.draggedPlacementId || this.draggedSlot !== group.slot || this.draggedPlacementId === targetPlacementId) {
                this.resetDragState();
                return;
            }

            const placementIds = group.items.map((item) => item.placement_id);
            const fromIndex = placementIds.indexOf(this.draggedPlacementId);
            const toIndex = placementIds.indexOf(targetPlacementId);

            if (fromIndex < 0 || toIndex < 0) {
                this.resetDragState();
                return;
            }

            placementIds.splice(fromIndex, 1);
            placementIds.splice(toIndex, 0, this.draggedPlacementId);

            const reorderedItems = placementIds
                .map((placementId) => group.items.find((item) => item.placement_id === placementId))
                .filter(Boolean);

            axios.patch(this.reorderEndpoint(group.slot), {
                placement_ids: placementIds,
            }).then(() => {
                this.$emit('reordered', {
                    slot: group.slot,
                    items: reorderedItems,
                });
            }).finally(() => {
                this.resetDragState();
            });
        },
        resetDragState() {
            this.draggedSlot = null;
            this.draggedPlacementId = null;
        },
        createEndpoint(slot) {
            if (this.contextType === 'module') {
                return 'api/workspace/admin/cms/modules/' + this.contextId + '/slots/' + slot + '/modules';
            }

            return 'api/workspace/admin/cms/pages/' + this.contextId + '/slots/' + slot + '/modules';
        },
        attachEndpoint(slot) {
            if (this.contextType === 'module') {
                return 'api/workspace/admin/cms/modules/' + this.contextId + '/slots/' + slot + '/placements';
            }

            return 'api/workspace/admin/cms/pages/' + this.contextId + '/slots/' + slot + '/placements';
        },
        reorderEndpoint(slot) {
            if (this.contextType === 'module') {
                return 'api/workspace/admin/cms/modules/' + this.contextId + '/slots/' + slot + '/placements/order';
            }

            return 'api/workspace/admin/cms/pages/' + this.contextId + '/slots/' + slot + '/placements/order';
        },
    },
};
</script>
